import React from "react";
import { readCommercialDomainsForRow, repeaterToArray } from "./commercialProjectAcf";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import {
  displayNameForMaterial,
  findCatalogMaterial,
  getEngineeringMaterialsCatalog,
  slugifyMaterialName,
  unitLabelForMaterialName,
} from "./engineeringMaterialsCatalog";

export function normalizeMaterialRows(raw, catalog = getEngineeringMaterialsCatalog()) {
  if (!Array.isArray(raw) && !(raw && typeof raw === "object")) return [];
  const list = Array.isArray(raw)
    ? raw
    : Object.keys(raw)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => raw[k]);
  return list
    .map((row) => {
      const r = row && typeof row === "object" ? row : {};
      const item = r.item != null ? String(r.item).trim() : "";
      const slugHint = r.slug != null ? String(r.slug).trim() : "";
      const quantity = r.quantity != null ? String(r.quantity).trim() : "";
      if (!item && !slugHint && !quantity) return null;
      const found = findCatalogMaterial(catalog, slugHint || item);
      const slug = found?.slug || slugHint || slugifyMaterialName(item);
      const name = found?.name || item || slug;
      return { slug, item: name, quantity };
    })
    .filter(Boolean);
}

function parseMaterialQuantity(raw) {
  const s = String(raw || "").replace(/,/g, "").trim();
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return null;
  return Number(s);
}

function materialIdentityKey(row) {
  const slug = String(row?.slug || "").trim().toLowerCase();
  if (slug) return slug;
  const item = String(row?.item || "").trim();
  return slugifyMaterialName(item) || item.toLowerCase();
}

/** Sum permit material lines by catalog slug (falls back to name for legacy rows). */
export function aggregateMaterialTotals(lineRows, catalog = getEngineeringMaterialsCatalog()) {
  const byKey = new Map();
  for (const row of normalizeMaterialRows(lineRows, catalog)) {
    const key = materialIdentityKey(row);
    if (!key) continue;
    const displayItem = displayNameForMaterial(row.slug || row.item, catalog) || row.item;
    const qty = parseMaterialQuantity(row.quantity);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, {
        slug: row.slug || key,
        item: displayItem,
        quantity: qty != null ? String(qty) : row.quantity,
      });
      continue;
    }
    const prevNum = parseMaterialQuantity(prev.quantity);
    if (prevNum != null && qty != null) {
      byKey.set(key, { slug: prev.slug, item: displayItem || prev.item, quantity: String(prevNum + qty) });
    }
  }
  return [...byKey.values()].sort((a, b) => a.item.localeCompare(b.item, undefined, { sensitivity: "base" }));
}

export function collectPermitMaterialsFromDomains(domains) {
  const eng = domains?.[COMMERCIAL_DOMAINS.ENGINEERING] || {};
  const permits = repeaterToArray(eng.permits);
  const rows = [];
  for (const permit of permits) {
    for (const mat of repeaterToArray(permit?.materials)) {
      const item = mat?.item != null ? String(mat.item).trim() : "";
      const slug = mat?.slug != null ? String(mat.slug).trim() : "";
      const quantity = mat?.quantity != null ? String(mat.quantity).trim() : "";
      if (!item && !slug && !quantity) continue;
      rows.push({ slug, item, quantity });
    }
  }
  return rows;
}

export function collectPermitMaterialsFromProjectRow(row) {
  if (!row) return [];
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  return collectPermitMaterialsFromDomains(domains);
}

/** Board-friendly rollup: "Conduit (1200); Handholes (2)". */
export function formatProjectMaterialsSummary(row) {
  const totals = aggregateMaterialTotals(collectPermitMaterialsFromProjectRow(row));
  if (!totals.length) return "";
  return totals
    .map((t) => (t.quantity ? `${t.item} (${t.quantity})` : t.item))
    .join("; ");
}

export function projectHasMaterialsReviewPending(row) {
  if (!row) return false;
  const domains = readCommercialDomainsForRow(row, row.acf_domains);
  const permits = repeaterToArray(domains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permits);
  return permits.some((p) => p?.materials_review_pending === true);
}

export function formatProjectMaterialsReview(row) {
  return projectHasMaterialsReviewPending(row) ? "Needs review" : "";
}

/** Roll up permit materials across expansion subsegment project rows. */
export function aggregateSegmentMaterialTotals(projectRows, { excludeParentShell = true } = {}) {
  const lines = [];
  for (const row of projectRows || []) {
    if (excludeParentShell) {
      const wo = String(row?.work_order ?? "").trim();
      if (wo && !wo.includes(".")) continue;
    }
    lines.push(...collectPermitMaterialsFromProjectRow(row));
  }
  return aggregateMaterialTotals(lines);
}

export function EngineeringMaterialsSummary({
  rows,
  limit = null,
  emptyMessage = "No materials entered.",
  metaSuffix = "",
}) {
  const lines = normalizeMaterialRows(rows);
  const filled = aggregateMaterialTotals(lines);
  if (!filled.length) {
    return <p className="project-detail-muted">{emptyMessage}</p>;
  }
  const shown = limit != null ? filled.slice(0, limit) : filled;
  const rolledUp = lines.length > filled.length;
  return (
    <div className="eng-materials-summary">
      <p className="eng-materials-summary-meta">
        {filled.length} material{filled.length === 1 ? "" : "s"}
        {rolledUp ? ` · totaled from ${lines.length} permit lines` : ""}
        {metaSuffix ? ` · ${metaSuffix}` : ""}
      </p>
      <table className="eng-materials-summary-table">
        <thead>
          <tr>
            <th scope="col">Material</th>
            <th scope="col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={`${r.slug || r.item}-${i}`}>
              <td>{r.item}</td>
              <td>
                {r.quantity}
                {r.quantity ? (
                  <span className="eng-materials-unit-hint"> {unitLabelForMaterialName(r.slug || r.item)}</span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {limit != null && filled.length > limit ? (
        <p className="project-detail-muted">
          + {filled.length - limit} more material{filled.length - limit === 1 ? "" : "s"}
        </p>
      ) : null}
    </div>
  );
}
